'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMicroSource } from '@akademi/site-builder-runtime';
import type { RendererProps } from '../types';
import { PopupHeader, padded } from '../chrome';

interface PollOption {
  id: string;
  label: string;
}

const ACCENT = 'var(--color-brand-600, #7c3aed)';
const BORDER = 'var(--color-border, #e2e8f0)';
const MUTED = 'var(--color-text-secondary, #64748b)';

/**
 * Opinion poll. Renders {@code body.question} + id-keyed
 * {@code body.options[]}; the learner picks one option (or several
 * when {@code body.allowMultiple}) and votes. The vote is recorded via
 * {@code MicroSource.polls.vote} and completion is signalled through
 * {@code onComplete} (the popup marks progress completed).
 *
 * <p>Results come from {@code MicroSource.polls.results}. To show the
 * learner's own vote instantly without a read-after-write race, the
 * tally is fetched BEFORE recording, then the chosen option(s) are
 * added optimistically on top. {@code body.showResults}:
 * {@code 'after_vote'} (default) reveals the bars once voted;
 * {@code 'always'} shows the live distribution up front while the
 * options stay votable.
 *
 * <p>A vote is not a separate record — {@code polls.vote} merges
 * {@code result.poll.choices} onto the learner's progress row. So the
 * previous answer is already on the row the popup fetched: it is
 * restored here on open, which both shows the learner what they picked
 * and closes the ballot. Without it the poll reopened votable and the
 * optimistic tally counted the same learner again.
 */
export function PollRenderer({ item, onComplete, progress }: RendererProps) {
  const { t } = useTranslation('site-builder-blocks');
  const micro = useMicroSource();
  const body =
    (item.body as {
      question?: string;
      questionDescription?: string | null;
      options?: PollOption[];
      allowMultiple?: boolean;
      showResults?: string;
    }) ?? {};
  const question = body.question ?? '';
  const questionDescription = body.questionDescription ?? null;
  const options = Array.isArray(body.options) ? body.options : [];
  const allowMultiple = body.allowMultiple === true;
  const showResultsAlways = body.showResults === 'always';

  const previousChoices = readStoredChoices(progress?.result);
  const alreadyVoted = previousChoices !== null;

  const [selected, setSelected] = useState<string[]>(previousChoices ?? []);
  const [voted, setVoted] = useState(alreadyVoted);
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  const [voters, setVoters] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchResults = async (): Promise<{
    counts: Record<string, number>;
    voters: number;
  } | null> => {
    if (!micro?.polls) return null;
    try {
      const data = await micro.polls.results(item.id);
      if (!data) return null;
      return { counts: data.counts ?? {}, voters: data.voters ?? 0 };
    } catch {
      return null;
    }
  };

  // The popup's progress read resolves after mount, so adopt the stored
  // ballot when it lands. Guarded on `alreadyVoted` so a fresh vote made
  // in this session is never overwritten by a stale row.
  useEffect(() => {
    if (previousChoices === null) return;
    setSelected(previousChoices);
    setVoted(true);
  }, [progress]);

  // Someone who has already voted still needs to see the distribution —
  // their ballot is closed, so the bars are all there is to show.
  useEffect(() => {
    if (!alreadyVoted) return;
    let alive = true;
    void fetchResults().then((r) => {
      if (alive && r) {
        setCounts(r.counts);
        setVoters(r.voters);
      }
    });
    return () => {
      alive = false;
    };
  }, [alreadyVoted]);

  // 'always' polls show the live distribution before voting.
  useEffect(() => {
    if (!showResultsAlways) return;
    let alive = true;
    void fetchResults().then((r) => {
      if (alive && r) {
        setCounts(r.counts);
        setVoters(r.voters);
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  const toggle = (id: string) => {
    if (voted) return;
    setSelected((prev) => {
      if (allowMultiple) return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      return [id];
    });
  };

  const submitVote = async () => {
    if (voted || selected.length === 0) return;
    setLoading(true);
    // Tally as it stands BEFORE our write, then add our pick(s) optimistically.
    const base = (await fetchResults()) ?? { counts: {}, voters: 0 };
    const merged: Record<string, number> = { ...base.counts };
    for (const id of selected) merged[id] = (merged[id] ?? 0) + 1;
    if (micro?.polls) {
      try {
        await micro.polls.vote({ itemId: item.id, choices: selected });
      } catch {
        /* vote write is best-effort — the local tally still renders */
      }
    }
    setCounts(merged);
    setVoters(base.voters + 1);
    setVoted(true);
    setLoading(false);
    onComplete();
  };

  const showBars = voted || showResultsAlways;
  const clickable = !voted;

  if (options.length === 0) {
    return (
      <div style={padded}>
        <PopupHeader kind={item.kind} title={item.title} description={item.description} />
        <div style={{ marginTop: 16, color: MUTED }}>{t('microPoll.empty')}</div>
      </div>
    );
  }

  return (
    <>
      <PopupHeader kind={item.kind} title={item.title} description={item.description} />
      <div style={{ ...padded, paddingTop: 0 }}>
        {question && (
          <div style={{ fontSize: 'var(--font-size-base, 15px)', fontWeight: 600 }}>{question}</div>
        )}
        {questionDescription && (
          <p style={{ margin: '6px 0 0', fontSize: 'var(--font-size-sm, 14px)', color: MUTED }}>
            {questionDescription}
          </p>
        )}

        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {options.map((opt) => {
            const isSelected = selected.includes(opt.id);
            const n = counts?.[opt.id] ?? 0;
            const pct = voters === 0 ? 0 : Math.round((n / voters) * 100);
            return (
              <div
                key={opt.id}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                aria-pressed={clickable ? isSelected : undefined}
                onClick={clickable ? () => toggle(opt.id) : undefined}
                onKeyDown={
                  clickable
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggle(opt.id);
                        }
                      }
                    : undefined
                }
                style={{
                  padding: 'var(--space-3, 12px) var(--space-3-5, 14px)',
                  border: `1px solid ${isSelected ? ACCENT : BORDER}`,
                  borderRadius: 10,
                  background: isSelected
                    ? 'rgba(124, 58, 237, 0.06)'
                    : 'var(--color-surface-card, #fff)',
                  cursor: clickable ? 'pointer' : 'default',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {!voted && <RadioDot selected={isSelected} />}
                  <span
                    style={{
                      flex: 1,
                      fontSize: 'var(--font-size-sm, 14px)',
                      fontWeight: isSelected ? 700 : 500,
                    }}
                  >
                    {opt.label}
                    {voted && isSelected ? ' ✓' : ''}
                  </span>
                  {showBars && (
                    <span
                      style={{
                        color: MUTED,
                        fontWeight: 600,
                        fontSize: '13px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {pct}% · {n}
                    </span>
                  )}
                </div>
                {showBars && <ResultBar value={n} max={voters} />}
              </div>
            );
          })}
        </div>

        {!voted && (
          <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              onClick={() => void submitVote()}
              disabled={selected.length === 0 || loading}
              style={{
                padding: 'var(--space-2-5, 10px) var(--space-5, 20px)',
                border: 'none',
                borderRadius: 8,
                background: 'var(--color-text-primary, #18181b)',
                color: 'var(--color-text-on-brand, #fff)',
                fontWeight: 600,
                cursor: selected.length === 0 || loading ? 'not-allowed' : 'pointer',
                opacity: selected.length === 0 || loading ? 0.5 : 1,
              }}
            >
              {loading ? t('microPoll.submitting') : t('microPoll.vote')}
            </button>
            {allowMultiple && (
              <span style={{ fontSize: '13px', color: MUTED }}>{t('microPoll.multiHint')}</span>
            )}
          </div>
        )}

        {showBars && (
          <p style={{ marginTop: 16, marginBottom: 0, fontSize: '13px', color: MUTED }}>
            {voted ? t('microPoll.votersWithYours', { voters }) : t('microPoll.voters', { voters })}
          </p>
        )}
      </div>
    </>
  );
}

function RadioDot({ selected }: { selected: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 18,
        height: 18,
        flexShrink: 0,
        borderRadius: '50%',
        border: `2px solid ${selected ? ACCENT : BORDER}`,
        display: 'inline-grid',
        placeItems: 'center',
      }}
    >
      {selected && (
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: ACCENT }} />
      )}
    </span>
  );
}

function ResultBar({ value, max }: { value: number; max: number }) {
  const pct = max === 0 ? 0 : Math.min(100, Math.round((value / max) * 100));
  return (
    <div
      style={{
        marginTop: 6,
        height: 6,
        borderRadius: 999,
        background: 'var(--color-surface-page, #f1f5f9)',
        overflow: 'hidden',
      }}
    >
      <div style={{ width: `${pct}%`, height: '100%', borderRadius: 999, background: ACCENT }} />
    </div>
  );
}

/**
 * Pulls a previously-stored ballot out of a progress row's {@code result}
 * echo — the same {@code result.poll.choices} shape {@code polls.vote}
 * writes. Returns {@code null} when the learner has not voted, which is
 * what distinguishes "no answer yet" from "voted for nothing".
 */
function readStoredChoices(result: Record<string, unknown> | undefined): string[] | null {
  const poll = result?.['poll'];
  if (!poll || typeof poll !== 'object') return null;
  const choices = (poll as { choices?: unknown }).choices;
  if (!Array.isArray(choices)) return null;
  return choices.map((c) => String(c));
}
